import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  ConfigProvider,
  DatePicker,
  Input,
  message,
  Popconfirm,
  Radio,
  Select,
  Table,
  Tag,
  theme,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import { getDinheiros, recolherDinheiros } from "@/services/dinheiro";
import { getUnidades } from "@/services/unidade";
import { DinheiroType } from "@/types/DinheiroType";
import { usePermissions } from "@/hooks/usePermissions";

dayjs.locale("pt-br");

const { RangePicker } = DatePicker;

const formatValor = (valor?: number) =>
  valor != null
    ? valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "-";

const formatData = (data?: string) => (data ? dayjs(data).format("DD/MM/YYYY") : "-");

type GrupoAluno = {
  key: string;
  alunoId: number;
  matricula?: number;
  unidadeNome?: string;
  nomeAluno?: string;
  quantidade: number;
  valorTotal: number;
  parcelas: DinheiroType[];
};

export default function ConsultaDinheiro() {
  const { canRecebimentoFinanceiro } = usePermissions();
  const [parcelas, setParcelas] = useState<DinheiroType[]>([]);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [unidadeIds, setUnidadeIds] = useState<number[]>([]);
  const [periodo, setPeriodo] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [busca, setBusca] = useState("");
  const [filtroRecolhimento, setFiltroRecolhimento] = useState<"todos" | "naoRecolhidos" | "recolhidos">("naoRecolhidos");

  const carregarParcelas = useCallback(async () => {
    try {
      const filtro = {
        recolhido:
          filtroRecolhimento === "todos" ? undefined : filtroRecolhimento === "recolhidos",
        unidadeIds: unidadeIds.length > 0 ? unidadeIds : undefined,
        dataInicio: periodo?.[0] ? periodo[0].format("YYYY-MM-DD") : undefined,
        dataFim: periodo?.[1] ? periodo[1].format("YYYY-MM-DD") : undefined,
        busca: busca.trim() || undefined,
      };
      const { data } = await getDinheiros(filtro);
      setParcelas(data ?? []);
    } catch {
      message.error("Erro ao carregar os pagamentos em dinheiro.");
    }
  }, [filtroRecolhimento, unidadeIds, periodo, busca]);

  useEffect(() => {
    carregarParcelas();
  }, [carregarParcelas]);

  useEffect(() => {
    getUnidades()
      .then(({ data }) => setUnidades(data ?? []))
      .catch(() => message.error("Erro ao carregar unidades."));
  }, []);

  const executar = async (acao: () => Promise<any>, sucesso: string) => {
    try {
      await acao();
      message.success(sucesso);
      carregarParcelas();
    } catch (error: any) {
      message.error(error?.response?.data ?? "Erro ao executar a operação.");
    }
  };

  const gruposAluno: GrupoAluno[] = useMemo(() => {
    const mapa = new Map<string, GrupoAluno>();
    parcelas.forEach((p) => {
      const chave = String(p.alunoId);
      const grupo = mapa.get(chave) ?? {
        key: chave,
        alunoId: p.alunoId,
        matricula: p.matricula,
        unidadeNome: p.unidadeNome,
        nomeAluno: p.nomeAluno,
        quantidade: 0,
        valorTotal: 0,
        parcelas: [],
      };
      grupo.quantidade += 1;
      grupo.valorTotal += p.valor ?? 0;
      grupo.parcelas.push(p);
      mapa.set(chave, grupo);
    });
    return Array.from(mapa.values());
  }, [parcelas]);

  const renderStatus = (registro: DinheiroType) =>
    registro.recolhido ? (
      <Tag color="green">Recolhido</Tag>
    ) : (
      <Tag color="default">Na secretaria</Tag>
    );

  const colunasGrupo: ColumnsType<GrupoAluno> = [
    { title: "Matrícula", dataIndex: "matricula", key: "matricula" },
    { title: "Unidade", dataIndex: "unidadeNome", key: "unidadeNome" },
    { title: "Aluno", dataIndex: "nomeAluno", key: "nomeAluno" },
    { title: "Qtd. Parcelas", dataIndex: "quantidade", key: "quantidade" },
    {
      title: "Valor Total",
      dataIndex: "valorTotal",
      key: "valorTotal",
      render: (v: number) => formatValor(v),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, grupo) => {
        const pendentes = grupo.parcelas.filter((p) => !p.recolhido);
        if (pendentes.length === 0) return <Tag color="green">Recolhidos</Tag>;
        if (!canRecebimentoFinanceiro) return null;
        return (
          <Popconfirm
            title="Confirmar recolhimento?"
            description={`Confirmar o recolhimento de ${pendentes.length} parcela(s) em dinheiro?`}
            okText="Sim"
            cancelText="Não"
            onConfirm={() =>
              executar(
                () => recolherDinheiros(pendentes.map((p) => p.id)),
                "Recolhimento confirmado com sucesso!"
              )
            }
          >
            <Button type="primary" size="small">
              Confirmar recolhimento
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const colunasParcelasDoGrupo: ColumnsType<DinheiroType> = [
    {
      title: "Status",
      key: "status",
      render: (_, registro) => renderStatus(registro),
    },
    { title: "Parcela", dataIndex: "parcela", key: "parcela" },
    {
      title: "Data",
      dataIndex: "dataParcela",
      key: "dataParcela",
      render: (v: string) => formatData(v),
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      render: (v: number) => formatValor(v),
    },
    {
      title: "Data Recolhimento",
      dataIndex: "dataRecolhimento",
      key: "dataRecolhimento",
      render: (v: string) => formatData(v),
    },
    { title: "Recolhido por", dataIndex: "recolhidoPorNome", key: "recolhidoPorNome" },
    {
      title: "Ação",
      key: "acao",
      render: (_, registro) =>
        !registro.recolhido && canRecebimentoFinanceiro ? (
          <Popconfirm
            title="Confirmar o recolhimento desta parcela?"
            okText="Sim"
            cancelText="Não"
            onConfirm={() =>
              executar(() => recolherDinheiros([registro.id]), "Recolhimento confirmado com sucesso!")
            }
          >
            <Button type="link" size="small">
              Confirmar recolhimento
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Controle de Dinheiro</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-center mb-6">
          <Input
            allowClear
            placeholder="Buscar por nome ou CPF"
            className="w-80"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select
            allowClear
            mode="multiple"
            placeholder="Todas as unidades"
            className="w-64"
            value={unidadeIds}
            onChange={(value) => setUnidadeIds(value)}
            options={unidades.map((u: any) => ({ value: u.id, label: u.nome }))}
          />
          <RangePicker
            format="DD/MM/YYYY"
            value={periodo}
            onChange={(datas) => setPeriodo(datas as [Dayjs | null, Dayjs | null] | null)}
            placeholder={["Data inicial", "Data final"]}
          />
        </div>

        <div className="mb-4">
          <Radio.Group
            value={filtroRecolhimento}
            onChange={(e) => setFiltroRecolhimento(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: "⏳ Não recolhidos", value: "naoRecolhidos" },
              { label: "✅ Recolhidos", value: "recolhidos" },
              { label: "Todos", value: "todos" },
            ]}
          />
        </div>

        <Table
          columns={colunasGrupo}
          dataSource={gruposAluno}
          rowKey="key"
          expandable={{
            expandedRowRender: (grupo) => (
              <Table
                columns={colunasParcelasDoGrupo}
                dataSource={grupo.parcelas}
                rowKey="id"
                pagination={false}
                size="small"
              />
            ),
          }}
        />
      </ConfigProvider>
    </>
  );
}
